import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  doc, 
  getDoc,
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import { useCompanyMembership } from './CompanyMembershipContext';

const DataContext = createContext();

export function useData() {
  return useContext(DataContext);
}

export function DataProvider({ children }) {
  const [customers, setCustomers] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [inventoryTransactions, setInventoryTransactions] = useState([]);
  const [userRole, setUserRole] = useState('user');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const { companyId, isLoading: companyLoading, membership } = useCompanyMembership();

  // Fetch data from Firebase
  useEffect(() => {
    const fetchData = async () => {
      // Wait for auth to finish loading
      if (!currentUser) {
        setLoading(false);
        return;
      }

      // Wait for membership to finish loading
      // If still loading, keep DataContext in loading state
      if (companyLoading) {
        console.log('[DataContext] Waiting for membership to load...');
        setLoading(true);
        return;
      }

      // Check if we should use old structure fallback
      // Only fallback if membership is definitively null (not just loading)
      // AND user doc doesn't exist or has no companyId
      if (!companyId && !companyLoading) {
        // Check if user doc exists - if it does and has no companyId, this is a legacy user
        // If it doesn't exist yet, we're in signup and should wait
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          
          if (!userDoc.exists()) {
            // User doc doesn't exist yet - likely during signup
            // Keep loading and wait for it to be created
            console.log('[DataContext] User document does not exist yet - waiting for signup to complete...');
            setLoading(true);
            return;
          }

          const userData = userDoc.data();
          if (!userData.companyId) {
            // User doc exists but no companyId - legacy user
            // TODO: This is for legacy users only - migrate to new structure
            console.log('[DataContext] Falling back to legacy structure (no companyId & no membership)');
            
            try {
              setLoading(true);
              setError(null);
              
              // Load from old top-level collections with userId filter
              const loadFromOld = async (collectionName) => {
                try {
                  const collectionRef = collection(db, collectionName);
                  const queryRef = query(collectionRef, where('userId', '==', currentUser.uid));
                  const snapshot = await getDocs(queryRef);
                  return snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                  }));
                } catch (err) {
                  console.error(`Error loading ${collectionName}:`, err);
                  return [];
                }
              };
              
              // Load all data from old structure in parallel
              const [customersData, estimatesData, jobsData, eventsData, teamsData, inventoryData, transactionsData] = await Promise.all([
                loadFromOld('customers'),
                loadFromOld('estimates'),
                loadFromOld('jobs'),
                loadFromOld('events'),
                loadFromOld('teams'),
                loadFromOld('inventory'),
                loadFromOld('inventoryTransactions'),
              ]);
              
              // Set the data from old structure
              setCustomers(customersData);
              setEstimates(estimatesData);
              setJobs(jobsData.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
              setEvents(eventsData);
              setTeams(teamsData);
              setInventory(inventoryData);
              setInventoryTransactions(transactionsData.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
              
              setLoading(false);
              
              console.log('[DataContext] ✅ Loaded data from legacy structure:', {
                customers: customersData.length,
                estimates: estimatesData.length,
                jobs: jobsData.length,
              });
              
              return;
            } catch (err) {
              console.error('[DataContext] Error loading from legacy structure:', err);
              setLoading(false);
              setError('Error loading data. Please refresh the page.');
              return;
            }
          } else {
            // User doc has companyId but membership not loaded yet - wait a bit
            console.log('[DataContext] User doc has companyId but membership not loaded - waiting...');
            setLoading(true);
            return;
          }
        } catch (err) {
          console.error('[DataContext] Error checking user document:', err);
          setLoading(true);
          return;
        }
      }

      // Use new company-scoped structure
      if (!companyId) {
        // Still waiting for companyId
        setLoading(true);
        return;
      }

      console.log('[DataContext] Using new company-scoped data for companyId:', companyId);
      

      try {
        setLoading(true);
        setError(null);

        // Fetch customers - now from company subcollection
        // Also load from old structure and merge (during migration period)
        try {
          // Try new structure first
          const customersRef = collection(db, 'companies', companyId, 'customers');
          const customersSnapshot = await getDocs(customersRef);
          let customersData = customersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // If new structure is empty, try old structure as fallback
          if (customersData.length === 0) {
            try {
              const customersQuery = query(
                collection(db, 'customers'),
                where('userId', '==', currentUser.uid)
              );
              const oldCustomersSnapshot = await getDocs(customersQuery);
              customersData = oldCustomersSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
            } catch (fallbackErr) {
              console.error('Error with fallback customers fetch:', fallbackErr);
            }
          }
          
          setCustomers(customersData);
        } catch (err) {
          console.error('Error fetching customers:', err);
          // Final fallback: try old structure
          try {
            const customersQuery = query(
              collection(db, 'customers'),
              where('userId', '==', currentUser.uid)
            );
            const customersSnapshot = await getDocs(customersQuery);
            const customersData = customersSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setCustomers(customersData);
          } catch (fallbackErr) {
            console.error('Error with fallback customers fetch:', fallbackErr);
            setCustomers([]);
          }
        }

        // Fetch estimates - now from company subcollection
        try {
          const estimatesRef = collection(db, 'companies', companyId, 'estimates');
          const estimatesSnapshot = await getDocs(estimatesRef);
          const estimatesData = estimatesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setEstimates(estimatesData);
        } catch (err) {
          console.error('Error fetching estimates:', err);
          // Fallback to old structure
          try {
            const estimatesQuery = query(
              collection(db, 'estimates'),
              where('userId', '==', currentUser.uid)
            );
            const estimatesSnapshot = await getDocs(estimatesQuery);
            const estimatesData = estimatesSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setEstimates(estimatesData);
          } catch (fallbackErr) {
            console.error('Error with fallback estimates fetch:', fallbackErr);
            setEstimates([]);
          }
        }

        // Fetch jobs - now from company subcollection
        try {
          const jobsRef = collection(db, 'companies', companyId, 'jobs');
          const jobsSnapshot = await getDocs(jobsRef);
          const jobsData = jobsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          setJobs(jobsData);
        } catch (err) {
          console.error('Error fetching jobs:', err);
          // Fallback to old structure
          try {
            const jobsQuery = query(
              collection(db, 'jobs'),
              where('userId', '==', currentUser.uid)
            );
            const jobsSnapshot = await getDocs(jobsQuery);
            const jobsData = jobsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
            setJobs(jobsData);
          } catch (fallbackErr) {
            console.error('Error with fallback jobs fetch:', fallbackErr);
            setJobs([]);
          }
        }

        // Fetch events - now from company subcollection
        try {
          const eventsRef = collection(db, 'companies', companyId, 'events');
          const eventsSnapshot = await getDocs(eventsRef);
          const eventsData = eventsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setEvents(eventsData);
        } catch (err) {
          console.error('Error fetching events:', err);
          // Fallback to old structure
          try {
            const eventsQuery = query(
              collection(db, 'events'),
              where('userId', '==', currentUser.uid)
            );
            const eventsSnapshot = await getDocs(eventsQuery);
            const eventsData = eventsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setEvents(eventsData);
          } catch (fallbackErr) {
            console.error('Error with fallback events fetch:', fallbackErr);
            setEvents([]);
          }
        }

        // Fetch teams - now from company subcollection
        try {
          const teamsRef = collection(db, 'companies', companyId, 'teams');
          const teamsSnapshot = await getDocs(teamsRef);
          const teamsData = teamsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setTeams(teamsData);
        } catch (err) {
          console.error('Error fetching teams:', err);
          // Fallback to old structure
          try {
            const teamsQuery = query(
              collection(db, 'teams'),
              where('userId', '==', currentUser.uid)
            );
            const teamsSnapshot = await getDocs(teamsQuery);
            const teamsData = teamsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setTeams(teamsData);
          } catch (fallbackErr) {
            console.error('Error with fallback teams fetch:', fallbackErr);
            setTeams([]);
          }
        }

        // Fetch inventory items - now from company subcollection
        try {
          const inventoryRef = collection(db, 'companies', companyId, 'inventory');
          const inventorySnapshot = await getDocs(inventoryRef);
          const inventoryData = inventorySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setInventory(inventoryData);
        } catch (err) {
          console.error('Error fetching inventory:', err);
          // Fallback to old structure
          try {
            const inventoryQuery = query(
              collection(db, 'inventory'),
              where('userId', '==', currentUser.uid)
            );
            const inventorySnapshot = await getDocs(inventoryQuery);
            const inventoryData = inventorySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setInventory(inventoryData);
          } catch (fallbackErr) {
            console.error('Error with fallback inventory fetch:', fallbackErr);
            setInventory([]);
          }
        }

        // Fetch inventory transactions - now from company subcollection
        try {
          const transactionsRef = collection(db, 'companies', companyId, 'inventoryTransactions');
          const transactionsSnapshot = await getDocs(transactionsRef);
          const transactionsData = transactionsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          setInventoryTransactions(transactionsData);
        } catch (err) {
          console.error('Error fetching inventory transactions:', err);
          // Fallback to old structure
          try {
            const transactionsQuery = query(
              collection(db, 'inventoryTransactions'),
              where('userId', '==', currentUser.uid)
            );
            const transactionsSnapshot = await getDocs(transactionsQuery);
            const transactionsData = transactionsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
            setInventoryTransactions(transactionsData);
          } catch (fallbackErr) {
            console.error('Error with fallback inventoryTransactions fetch:', fallbackErr);
            setInventoryTransactions([]);
          }
        }

      } catch (err) {
        console.error('Error in fetchData:', err);
        // Only set error if it's a critical error, not individual query failures
        if (err.code === 'permission-denied') {
          setError('Permission denied. Please check your Firestore security rules.');
        } else if (err.code === 'unavailable') {
          setError('Firebase is unavailable. Please check your internet connection.');
        } else {
          // Don't set a general error since individual queries handle their own errors
          console.warn('Non-critical error in fetchData:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, companyId, companyLoading]);

  // Customer management functions
  const addCustomer = async (customerData) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      // Validate required fields
      if (!customerData.name || !customerData.email) {
        throw new Error('Name and email are required');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customerData.email)) {
        throw new Error('Invalid email format');
      }

      // Use company subcollection - no userId field needed (scoped by path)
      const docRef = await addDoc(collection(db, 'companies', companyId, 'customers'), {
        ...customerData,
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString()
      });
      const newCustomer = { id: docRef.id, ...customerData, createdBy: currentUser.uid };
      setCustomers(prev => [...prev, newCustomer]);
      return newCustomer;
    } catch (err) {
      console.error('Error adding customer:', err);
      throw err;
    }
  };

  const updateCustomer = async (id, customerData) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      const customerRef = doc(db, 'companies', companyId, 'customers', id);
      await updateDoc(customerRef, {
        ...customerData,
        updatedAt: new Date().toISOString()
      });
      setCustomers(prev => prev.map(c => c.id === id ? { ...c, ...customerData } : c));
    } catch (err) {
      console.error('Error updating customer:', err);
      throw err;
    }
  };

  const deleteCustomer = async (id) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      await deleteDoc(doc(db, 'companies', companyId, 'customers', id));
      setCustomers(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error('Error deleting customer:', err);
      throw err;
    }
  };

  // Estimate management functions
  // Helper function to remove undefined values from objects (Firestore doesn't allow undefined)
  const removeUndefinedValues = (obj) => {
    if (obj === null || typeof obj !== 'object' || obj instanceof Date) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => removeUndefinedValues(item));
    }
    
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedValues(value);
      }
    }
    return cleaned;
  };

  const addEstimate = async (estimateData) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      // Validate required fields
      if (!estimateData.customerId || !estimateData.total) {
        throw new Error('Customer and total amount are required');
      }

      // Validate total amount
      if (isNaN(estimateData.total) || estimateData.total <= 0) {
        throw new Error('Invalid total amount');
      }

      // Get all estimates for the company
      const estimatesRef = collection(db, 'companies', companyId, 'estimates');
      const estimatesSnapshot = await getDocs(estimatesRef);
      
      // Generate the next estimate ID
      let nextEstimateId;
      if (estimatesSnapshot.empty) {
        nextEstimateId = 'E0001';
      } else {
        // Find the highest estimate ID
        const estimateIds = estimatesSnapshot.docs
          .map(doc => doc.data().estimateId)
          .filter(id => id && id.startsWith('E')) // Only consider valid estimate IDs
          .sort();
        
        if (estimateIds.length === 0) {
          nextEstimateId = 'E0001';
        } else {
          const lastId = estimateIds.pop();
          const lastNumber = parseInt(lastId.substring(1));
          if (isNaN(lastNumber)) {
            nextEstimateId = 'E0001';
          } else {
            nextEstimateId = `E${String(lastNumber + 1).padStart(4, '0')}`;
          }
        }
      }

      // Remove undefined values before saving (Firestore doesn't allow undefined)
      // Also add companyId to the document for collectionGroup queries to work
      const cleanedEstimateData = removeUndefinedValues({
        ...estimateData,
        estimateId: nextEstimateId,
        companyId: companyId, // Store companyId in document for collectionGroup queries
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString(),
        status: 'Created'
      });

      // Add the estimate with the generated ID - use company subcollection
      const docRef = await addDoc(estimatesRef, cleanedEstimateData);

      const newEstimate = {
        id: docRef.id,
        estimateId: nextEstimateId,
        ...estimateData,
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString(),
        status: 'Created'
      };

      setEstimates(prev => [...prev, newEstimate]);
      return newEstimate;
    } catch (err) {
      console.error('Error adding estimate:', err);
      throw err;
    }
  };

  const updateEstimate = async (id, estimateData) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      const estimateRef = doc(db, 'companies', companyId, 'estimates', id);
      
      // Remove undefined values before saving (Firestore doesn't allow undefined)
      const cleanedEstimateData = removeUndefinedValues({
        ...estimateData,
        updatedAt: new Date().toISOString()
      });
      
      const updateData = cleanedEstimateData;
      
      // Remove any undefined values
      Object.keys(updateData).forEach(key => 
        updateData[key] === undefined && delete updateData[key]
      );

      await updateDoc(estimateRef, updateData);
      setEstimates(prev => prev.map(e => e.id === id ? { ...e, ...updateData } : e));
      return { ...estimateData, id };
    } catch (err) {
      console.error('Error updating estimate:', err);
      throw err;
    }
  };

  const deleteEstimate = async (id) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      const estimate = estimates.find(e => e.id === id);
      if (estimate && estimate.status === 'Converted to Job' && userRole !== 'admin') {
        throw new Error('Only administrators can delete estimates that have been converted to jobs');
      }
      await deleteDoc(doc(db, 'companies', companyId, 'estimates', id));
      setEstimates(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error('Error deleting estimate:', err);
      throw err;
    }
  };

  // Job management functions
  const addJob = async (jobData) => {
    if (!companyId) {
      throw new Error('Company not loaded. Please wait a moment and try again.');
    }

    if (!jobData.customerId || !jobData.estimateId) {
      throw new Error('Customer ID and Estimate ID are required');
    }

    // Find the estimate
    const estimate = estimates.find(e => e.id === jobData.estimateId);
    if (!estimate) {
      throw new Error('Estimate not found');
    }

    // Get all jobs for this company to generate the next job ID
    const jobsRef = collection(db, 'companies', companyId, 'jobs');
    const jobsSnapshot = await getDocs(jobsRef);
    const jobsData = jobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Find the highest existing job ID
    const jobIds = jobsData.map(job => {
      const match = job.jobId?.match(/J(\d+)/);
      return match ? parseInt(match[1]) : 0;
    });
    const highestId = Math.max(...jobIds, 0);
    
    // Generate the next job ID
    const nextId = highestId + 1;
    const jobId = `J${nextId.toString().padStart(4, '0')}`;

    // Set default dates if not provided
    const startDate = jobData.startDate || new Date().toISOString();
    const endDate = jobData.endDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    // Prepare job data with all required fields - use createdBy instead of userId
    const newJob = {
      ...jobData,
      jobId,
      createdBy: currentUser.uid,
      status: 'Not Started',
      startDate,
      endDate,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      workItems: Array.isArray(jobData.workItems) ? jobData.workItems : [],
      notes: jobData.notes || [],
      payments: jobData.payments || [],
      attachments: jobData.attachments || [],
      total: jobData.total || estimate.total || 0,
      remainingBalance: jobData.total || estimate.total || 0,
      totalPaid: 0
    };

    // Add the new job to Firestore - use company subcollection
    const docRef = await addDoc(jobsRef, newJob);
    
    // Update local state
    setJobs(prev => [...prev, { ...newJob, id: docRef.id }]);
    
    return docRef.id;
  };

  const updateJob = async (jobId, jobData) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      const jobIndex = jobs.findIndex(job => job.id === jobId);
      if (jobIndex === -1) {
        throw new Error('Job not found');
      }

      const updatedJob = {
        ...jobs[jobIndex],
        ...jobData,
        updatedAt: new Date().toISOString()
      };

      // Update in Firestore - use company subcollection
      const jobRef = doc(db, 'companies', companyId, 'jobs', jobId);
      await updateDoc(jobRef, {
        ...jobData,
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setJobs(prev => prev.map(job => job.id === jobId ? updatedJob : job));
      return updatedJob;
    } catch (error) {
      console.error('Error updating job:', error);
      throw error;
    }
  };

  const deleteJob = async (jobId) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      const job = jobs.find(job => job.id === jobId);
      if (!job) {
        throw new Error('Job not found');
      }

      // Check if job is in progress or completed
      if (job.status === 'In Progress' || job.status === 'Completed') {
        throw new Error('Cannot delete a job that is in progress or completed');
      }

      await deleteDoc(doc(db, 'companies', companyId, 'jobs', jobId));
      setJobs(prev => prev.filter(job => job.id !== jobId));
    } catch (error) {
      console.error('Error deleting job:', error);
      throw error;
    }
  };

  // Event management functions
  const addEvent = async (eventData) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      // Validate required fields
      if (!eventData.title || !eventData.startDate || !eventData.endDate) {
        throw new Error('Title, start date, and end date are required');
      }

      // Validate dates
      const startDate = new Date(eventData.startDate);
      const endDate = new Date(eventData.endDate);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        throw new Error('Invalid date format');
      }

      if (endDate < startDate) {
        throw new Error('End date must be after start date');
      }

      const docRef = await addDoc(collection(db, 'companies', companyId, 'events'), {
        ...eventData,
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString()
      });
      const newEvent = { id: docRef.id, ...eventData, createdBy: currentUser.uid };
      setEvents(prev => [...prev, newEvent]);
      return newEvent;
    } catch (err) {
      console.error('Error adding event:', err);
      throw err;
    }
  };

  const updateEvent = async (id, eventData) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      const eventRef = doc(db, 'companies', companyId, 'events', id);
      await updateDoc(eventRef, {
        ...eventData,
        updatedAt: new Date().toISOString()
      });
      setEvents(prev => prev.map(e => e.id === id ? { ...e, ...eventData } : e));
    } catch (err) {
      console.error('Error updating event:', err);
      throw err;
    }
  };

  const deleteEvent = async (id) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      await deleteDoc(doc(db, 'companies', companyId, 'events', id));
      setEvents(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error('Error deleting event:', err);
      throw err;
    }
  };

  // Team management functions
  const addTeam = async (teamData) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      const docRef = await addDoc(collection(db, 'companies', companyId, 'teams'), {
        ...teamData,
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      const newTeam = { id: docRef.id, ...teamData, createdBy: currentUser.uid };
      setTeams(prev => [...prev, newTeam]);
      return newTeam;
    } catch (err) {
      console.error('Error adding team:', err);
      throw err;
    }
  };

  const updateTeam = async (id, teamData) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      const teamRef = doc(db, 'companies', companyId, 'teams', id);
      await updateDoc(teamRef, {
        ...teamData,
        updatedAt: new Date().toISOString()
      });
      setTeams(prev => prev.map(t => t.id === id ? { ...t, ...teamData } : t));
    } catch (err) {
      console.error('Error updating team:', err);
      throw err;
    }
  };

  const deleteTeam = async (id) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      await deleteDoc(doc(db, 'companies', companyId, 'teams', id));
      setTeams(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('Error deleting team:', err);
      throw err;
    }
  };

  // Inventory management functions
  const addInventoryItem = async (itemData) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      if (!itemData.name || !itemData.category) {
        throw new Error('Name and category are required');
      }

      const inventoryData = {
        name: itemData.name,
        sku: itemData.sku || '',
        description: itemData.description || '',
        category: itemData.category,
        totalQuantity: itemData.totalQuantity || itemData.quantity || 0,
        checkedOutQuantity: 0,
        quantity: itemData.totalQuantity || itemData.quantity || 0, // For backward compatibility
        unit: itemData.unit || '',
        unitPrice: itemData.unitPrice || 0,
        lowStockThreshold: itemData.lowStockThreshold || 10,
        location: itemData.location || '',
        supplier: itemData.supplier || '',
        notes: itemData.notes || '',
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'companies', companyId, 'inventory'), inventoryData);
      
      const newItem = { 
        id: docRef.id, 
        ...inventoryData
      };
      setInventory(prev => [...prev, newItem]);
      return newItem;
    } catch (err) {
      console.error('Error adding inventory item:', err);
      // Provide more specific error messages
      if (err.code === 'permission-denied') {
        throw new Error('Permission denied. Please check your Firestore security rules.');
      } else if (err.code === 'unavailable') {
        throw new Error('Firebase is unavailable. Please check your internet connection.');
      } else if (err.message) {
        throw err;
      } else {
        throw new Error('Failed to add inventory item. Please try again.');
      }
    }
  };

  const updateInventoryItem = async (id, itemData) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      // Get existing item from state to preserve fields not being updated
      const existingItem = inventory.find(item => item.id === id);
      if (!existingItem) {
        throw new Error('Inventory item not found');
      }

      const itemRef = doc(db, 'companies', companyId, 'inventory', id);
      
      // Build update data by merging with existing item, only updating provided fields
      const updateData = {
        ...existingItem, // Start with existing data
        updatedAt: new Date().toISOString()
      };

      // Only update fields that are explicitly provided in itemData
      if (itemData.name !== undefined) updateData.name = itemData.name;
      if (itemData.sku !== undefined) updateData.sku = itemData.sku || '';
      if (itemData.description !== undefined) updateData.description = itemData.description || '';
      if (itemData.category !== undefined) updateData.category = itemData.category;
      if (itemData.unit !== undefined) updateData.unit = itemData.unit || '';
      if (itemData.unitPrice !== undefined) updateData.unitPrice = itemData.unitPrice || 0;
      if (itemData.lowStockThreshold !== undefined) updateData.lowStockThreshold = itemData.lowStockThreshold || 10;
      if (itemData.location !== undefined) updateData.location = itemData.location || '';
      if (itemData.supplier !== undefined) updateData.supplier = itemData.supplier || '';
      if (itemData.notes !== undefined) updateData.notes = itemData.notes || '';
      if (itemData.checkedOutQuantity !== undefined) updateData.checkedOutQuantity = itemData.checkedOutQuantity;
      
      // Handle quantity/totalQuantity - only update if provided
      if (itemData.totalQuantity !== undefined) {
        updateData.totalQuantity = itemData.totalQuantity;
        updateData.quantity = itemData.totalQuantity - (updateData.checkedOutQuantity || 0);
      } else if (itemData.quantity !== undefined) {
        updateData.quantity = itemData.quantity;
        // If totalQuantity wasn't provided but quantity was, preserve totalQuantity from existing item
        if (updateData.totalQuantity === undefined) {
          updateData.totalQuantity = existingItem.totalQuantity || existingItem.quantity || 0;
        }
      }

      await updateDoc(itemRef, updateData);
      setInventory(prev => prev.map(item => item.id === id ? { ...item, ...updateData } : item));
    } catch (err) {
      console.error('Error updating inventory item:', err);
      // Provide more specific error messages
      if (err.code === 'permission-denied') {
        throw new Error('Permission denied. Please check your Firestore security rules.');
      } else if (err.code === 'unavailable') {
        throw new Error('Firebase is unavailable. Please check your internet connection.');
      } else if (err.code === 'not-found') {
        throw new Error('Inventory item not found.');
      } else if (err.message) {
        throw err;
      } else {
        throw new Error('Failed to update inventory item. Please try again.');
      }
    }
  };

  const deleteInventoryItem = async (id) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }

      await deleteDoc(doc(db, 'companies', companyId, 'inventory', id));
      setInventory(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      console.error('Error deleting inventory item:', err);
      throw err;
    }
  };

  // Inventory checkout/checkin functions
  const checkoutInventoryItem = async (itemId, jobId, quantity, notes = '') => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }
      if (!itemId || !jobId || !quantity || quantity <= 0) {
        throw new Error('Item ID, Job ID, and valid quantity are required');
      }

      const item = inventory.find(i => i.id === itemId);
      if (!item) {
        throw new Error('Inventory item not found');
      }

      const totalQty = item.totalQuantity || item.quantity || 0;
      const checkedOut = item.checkedOutQuantity || 0;
      const available = totalQty - checkedOut;

      if (quantity > available) {
        throw new Error(`Insufficient available stock. Only ${available} available.`);
      }

      const newCheckedOut = checkedOut + quantity;

      // Update inventory item
      await updateInventoryItem(itemId, { 
        checkedOutQuantity: newCheckedOut,
        quantity: totalQty - newCheckedOut // For backward compatibility
      });

      // Create checkout transaction - use company subcollection
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }
      const docRef = await addDoc(collection(db, 'companies', companyId, 'inventoryTransactions'), {
        itemId,
        jobId,
        type: 'checkout',
        quantity,
        notes,
        createdBy: currentUser.uid,
        previousCheckedOut: checkedOut,
        newCheckedOut: newCheckedOut,
        createdAt: new Date().toISOString()
      });

      const newTransaction = { id: docRef.id, itemId, jobId, type: 'checkout', quantity, notes };
      setInventoryTransactions(prev => [newTransaction, ...prev]);
      return newTransaction;
    } catch (err) {
      console.error('Error checking out inventory item:', err);
      throw err;
    }
  };

  const checkinInventoryItem = async (itemId, jobId, quantity, notes = '') => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }
      if (!itemId || !jobId || !quantity || quantity <= 0) {
        throw new Error('Item ID, Job ID, and valid quantity are required');
      }

      const item = inventory.find(i => i.id === itemId);
      if (!item) {
        throw new Error('Inventory item not found');
      }

      // Check how much is actually checked out to this job
      const jobCheckouts = inventoryTransactions.filter(
        t => t.itemId === itemId && t.jobId === jobId && t.type === 'checkout'
      );
      const jobCheckins = inventoryTransactions.filter(
        t => t.itemId === itemId && t.jobId === jobId && t.type === 'checkin'
      );
      const totalCheckedOutToJob = jobCheckouts.reduce((sum, t) => sum + (t.quantity || 0), 0);
      const totalCheckedInFromJob = jobCheckins.reduce((sum, t) => sum + (t.quantity || 0), 0);
      const currentlyCheckedOutToJob = totalCheckedOutToJob - totalCheckedInFromJob;

      if (quantity > currentlyCheckedOutToJob) {
        throw new Error(`Cannot check in more than what's checked out. Only ${currentlyCheckedOutToJob} checked out to this job.`);
      }

      const checkedOut = item.checkedOutQuantity || 0;
      const newCheckedOut = Math.max(0, checkedOut - quantity);

      // Update inventory item
      await updateInventoryItem(itemId, { 
        checkedOutQuantity: newCheckedOut,
        quantity: (item.totalQuantity || item.quantity || 0) - newCheckedOut
      });

      // Create checkin transaction - use company subcollection
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }
      const docRef = await addDoc(collection(db, 'companies', companyId, 'inventoryTransactions'), {
        itemId,
        jobId,
        type: 'checkin',
        quantity,
        notes,
        createdBy: currentUser.uid,
        previousCheckedOut: checkedOut,
        newCheckedOut: newCheckedOut,
        createdAt: new Date().toISOString()
      });

      const newTransaction = { id: docRef.id, itemId, jobId, type: 'checkin', quantity, notes };
      setInventoryTransactions(prev => [newTransaction, ...prev]);
      return newTransaction;
    } catch (err) {
      console.error('Error checking in inventory item:', err);
      throw err;
    }
  };

  // Simplified checkout for pick lists (no job required)
  const checkoutInventoryItemForPickList = async (itemId, quantity, notes = '', pickListNumber = null) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }
      if (!itemId || !quantity || quantity <= 0) {
        throw new Error('Item ID and valid quantity are required');
      }

      const item = inventory.find(i => i.id === itemId);
      if (!item) {
        throw new Error('Inventory item not found');
      }

      const totalQty = item.totalQuantity || item.quantity || 0;
      const checkedOut = item.checkedOutQuantity || 0;
      const available = totalQty - checkedOut;

      if (quantity > available) {
        throw new Error(`Insufficient available stock. Only ${available} available for "${item.name}".`);
      }

      const newCheckedOut = checkedOut + quantity;

      // Update inventory item
      await updateInventoryItem(itemId, { 
        checkedOutQuantity: newCheckedOut,
        quantity: totalQty - newCheckedOut
      });

      // Create checkout transaction (use 'pick-list' as jobId for internal tracking)
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }
      const transactionData = {
        itemId,
        jobId: 'pick-list', // Special identifier for pick list checkouts
        type: 'checkout',
        quantity,
        notes: notes || 'Checked out via pick list',
        createdBy: currentUser.uid,
        previousCheckedOut: checkedOut,
        newCheckedOut: newCheckedOut,
        createdAt: new Date().toISOString()
      };

      // Add pick list number if provided
      if (pickListNumber) {
        transactionData.pickListNumber = pickListNumber;
      }

      const docRef = await addDoc(collection(db, 'companies', companyId, 'inventoryTransactions'), transactionData);

      const newTransaction = { id: docRef.id, ...transactionData };
      setInventoryTransactions(prev => [newTransaction, ...prev]);
      return newTransaction;
    } catch (err) {
      console.error('Error checking out inventory item for pick list:', err);
      throw err;
    }
  };

  // Simplified checkin for pick lists (no job required - checks in from pick-list)
  const checkinInventoryItemFromPickList = async (itemId, quantity, notes = '', pickListNumber = null) => {
    try {
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }
      if (!itemId || !quantity || quantity <= 0) {
        throw new Error('Item ID and valid quantity are required');
      }

      const item = inventory.find(i => i.id === itemId);
      if (!item) {
        throw new Error('Inventory item not found');
      }

      // Check how much is actually checked out via pick lists
      const pickListCheckouts = inventoryTransactions.filter(
        t => t.itemId === itemId && t.jobId === 'pick-list' && t.type === 'checkout'
      );
      const pickListCheckins = inventoryTransactions.filter(
        t => t.itemId === itemId && t.jobId === 'pick-list' && t.type === 'checkin'
      );
      const totalCheckedOut = pickListCheckouts.reduce((sum, t) => sum + (t.quantity || 0), 0);
      const totalCheckedIn = pickListCheckins.reduce((sum, t) => sum + (t.quantity || 0), 0);
      const currentlyCheckedOut = totalCheckedOut - totalCheckedIn;

      if (quantity > currentlyCheckedOut) {
        throw new Error(`Cannot check in more than what's checked out. Only ${currentlyCheckedOut} checked out via pick lists for "${item.name}".`);
      }

      const checkedOut = item.checkedOutQuantity || 0;
      const newCheckedOut = Math.max(0, checkedOut - quantity);

      // Update inventory item
      await updateInventoryItem(itemId, { 
        checkedOutQuantity: newCheckedOut,
        quantity: (item.totalQuantity || item.quantity || 0) - newCheckedOut
      });

      // Create checkin transaction - use company subcollection
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }
      const transactionData = {
        itemId,
        jobId: 'pick-list', // Special identifier for pick list checkins
        type: 'checkin',
        quantity,
        notes: notes || 'Checked in from pick list',
        createdBy: currentUser.uid,
        previousCheckedOut: checkedOut,
        newCheckedOut: newCheckedOut,
        createdAt: new Date().toISOString()
      };

      // Add pick list number if provided
      if (pickListNumber) {
        transactionData.pickListNumber = pickListNumber;
      }

      const docRef = await addDoc(collection(db, 'companies', companyId, 'inventoryTransactions'), transactionData);

      const newTransaction = { id: docRef.id, ...transactionData };
      setInventoryTransactions(prev => [newTransaction, ...prev]);
      return newTransaction;
    } catch (err) {
      console.error('Error checking in inventory item from pick list:', err);
      throw err;
    }
  };

  // Legacy transaction function (for adding/removing total stock)
  const addInventoryTransaction = async (transactionData) => {
    try {
      if (!transactionData.itemId || !transactionData.type || !transactionData.quantity) {
        throw new Error('Item ID, type, and quantity are required');
      }

      const item = inventory.find(i => i.id === transactionData.itemId);
      if (!item) {
        throw new Error('Inventory item not found');
      }

      const totalQty = item.totalQuantity || item.quantity || 0;
      let newTotalQuantity = totalQty;

      if (transactionData.type === 'in') {
        newTotalQuantity = totalQty + transactionData.quantity;
      } else if (transactionData.type === 'out') {
        newTotalQuantity = totalQty - transactionData.quantity;
        if (newTotalQuantity < 0) {
          throw new Error('Insufficient stock');
        }
      } else if (transactionData.type === 'adjust') {
        newTotalQuantity = transactionData.quantity;
      }

      // Update inventory item
      await updateInventoryItem(transactionData.itemId, { 
        totalQuantity: newTotalQuantity,
        quantity: newTotalQuantity - (item.checkedOutQuantity || 0)
      });

      // Create transaction record - use company subcollection
      if (!companyId) {
        throw new Error('Company not loaded. Please wait a moment and try again.');
      }
      const docRef = await addDoc(collection(db, 'companies', companyId, 'inventoryTransactions'), {
        ...transactionData,
        createdBy: currentUser.uid,
        previousQuantity: totalQty,
        newQuantity: newTotalQuantity,
        createdAt: new Date().toISOString()
      });

      const newTransaction = { id: docRef.id, ...transactionData };
      setInventoryTransactions(prev => [newTransaction, ...prev]);
      return newTransaction;
    } catch (err) {
      console.error('Error adding inventory transaction:', err);
      throw err;
    }
  };

  const value = {
    customers,
    estimates,
    jobs,
    events,
    teams,
    inventory,
    inventoryTransactions,
    userRole,
    loading,
    error,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addEstimate,
    updateEstimate,
    deleteEstimate,
    addJob,
    updateJob,
    deleteJob,
    addEvent,
    updateEvent,
    deleteEvent,
    addTeam,
    updateTeam,
    deleteTeam,
    addInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
    addInventoryTransaction,
    checkoutInventoryItem,
    checkinInventoryItem,
    checkoutInventoryItemForPickList,
    checkinInventoryItemFromPickList
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
} 